/**
 * Shared color constants + the custom vertical-marker Chart.js plugin used
 * by both `Rsi2CandleChart` and `Rsi2RsiPanel` (Investment Ideas — RSI(2)
 * Oversold Pullback read-only research Candle Chart feature).
 *
 * There is no `chartjs-plugin-annotation` package installed in this
 * codebase (see `PayoffChart.tsx`'s own docstring on the same constraint),
 * so a signal-date / target-hit-date vertical line is implemented as a
 * small inline Chart.js plugin object with an `afterDraw` hook, using
 * `chart.scales.x.getPixelForValue()` — the exact same pixel-sync
 * technique `PayoffChart.tsx` already uses for its overlay geometry.
 */
import type { Chart, Plugin } from "chart.js";

export const COLOR_WICK = "#616161";
export const COLOR_BODY_UP = "#1B5E20";
export const COLOR_BODY_DOWN = "#C62828";
export const COLOR_SMA_50 = "#1976D2";
export const COLOR_SMA_200 = "#6A1B9A";
export const COLOR_REFERENCE = "#455A64";
export const COLOR_TARGET = "#1B5E20";
export const COLOR_RISK = "#C62828";
export const COLOR_SIGNAL_MARKER = "#455A64";
export const COLOR_TARGET_HIT_MARKER = "#1B5E20";
export const COLOR_RSI_LINE = "#6A1B9A";
export const COLOR_RSI_OVERSOLD = "#C62828";

export interface VerticalMarker {
  index: number;
  color: string;
  label: string;
}

/**
 * Converts a backend trade-date label (`"YYYY-MM-DD"`) to `"DD/MM/YY"` for
 * display on both charts' x-axis. Falls back to the raw string unchanged if
 * it isn't in the expected shape, rather than throwing -- this runs inside a
 * Chart.js tick callback, where a thrown error blanks the whole axis.
 */
export function formatDateDDMMYY(isoDate: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(isoDate);
  if (!match) return isoDate;
  const [, year, month, day] = match;
  return `${day}/${month}/${year.slice(2)}`;
}

// When two markers' labels would land within this many pixels of each
// other horizontally (e.g. a signal that resolves to Target Hit just 1-2
// trading days later, common for this strategy), the later label is
// dropped down a row instead of overlapping the earlier one.
const LABEL_COLLISION_PX = 46;
const LABEL_ROW_HEIGHT_PX = 12;

/**
 * Draws a dashed vertical line + small label at the pixel position of each
 * marker's category index. `markers` is read fresh off the plugin options
 * on every draw (not captured at registration time), so the SAME plugin
 * instance can be reused across re-renders with new marker indices.
 *
 * Vertical lines are always drawn at full chart height for every marker.
 * Labels are staggered onto additional rows (stacked downward) whenever
 * two markers' x-positions are close enough that their text would
 * otherwise overlap -- this is the only thing that changed here; marker
 * positions/colors/meaning are untouched.
 */
export function buildVerticalMarkerPlugin(pluginId: string): Plugin<"bar" | "line"> {
  return {
    id: pluginId,
    afterDraw: (chart: Chart) => {
      const markers: VerticalMarker[] | undefined = (
        chart.options.plugins as unknown as Record<string, { markers?: VerticalMarker[] }>
      )?.[pluginId]?.markers;
      if (!markers || markers.length === 0) return;

      const { ctx, chartArea, scales } = chart;
      const xScale = scales.x;
      if (!xScale || !chartArea) return;

      // Resolve pixel positions once, then sort left-to-right so row
      // assignment below only ever has to look at the previous label.
      const positioned = markers
        .filter((m) => m.index >= 0)
        .map((m) => ({ marker: m, x: xScale.getPixelForValue(m.index) }))
        .filter((p) => p.x >= chartArea.left && p.x <= chartArea.right)
        .sort((a, b) => a.x - b.x);

      ctx.save();

      // Lines: always full-height, independent of label collisions.
      for (const { marker, x } of positioned) {
        ctx.beginPath();
        ctx.setLineDash([4, 3]);
        ctx.strokeStyle = marker.color;
        ctx.lineWidth = 1.5;
        ctx.moveTo(x, chartArea.top);
        ctx.lineTo(x, chartArea.bottom);
        ctx.stroke();
      }
      ctx.setLineDash([]);

      // Labels: stack onto a new row whenever the previous label's x is
      // within LABEL_COLLISION_PX, so close-together markers (e.g. Setup
      // and Target Hit one trading day apart) never render on top of
      // each other.
      let previousX: number | null = null;
      let row = 0;
      ctx.font = "10px sans-serif";
      ctx.textAlign = "left";
      for (const { marker, x } of positioned) {
        row = previousX !== null && x - previousX < LABEL_COLLISION_PX ? row + 1 : 0;
        previousX = x;

        ctx.fillStyle = marker.color;
        ctx.fillText(marker.label, x + 4, chartArea.top + 10 + row * LABEL_ROW_HEIGHT_PX);
      }
      ctx.restore();
    },
  };
}
